import { useEffect, useMemo, useState } from 'react'
import { lireSessions } from '../../../../lib/groupes/groupes.supabase'
import {
  FILTRE_VIDE,
  filtrerSessions,
  periodesDisponibles,
  semaineParDefaut,
  semainesDisponibles,
  type Filtre,
  type SessionGroupe,
} from '../../../../lib/groupes/sessions'

/** Aujourd'hui à Bruxelles, en AAAA-MM-JJ : c'est le calendrier des stages. */
function aujourdhui(): string {
  return new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Brussels' })
}

/**
 * Les sessions, le filtre centre / période / semaine, et ce qu'il laisse voir.
 *
 * Sans filtre imposé, l'écran s'ouvre sur la semaine du prochain stage.
 */
export function useSessionsGroupes(filtreInitial?: Partial<Filtre>) {
  const [sessions, setSessions] = useState<SessionGroupe[]>([])
  const [chargement, setChargement] = useState(true)
  const [erreur, setErreur] = useState<string | null>(null)
  const [filtre, setFiltre] = useState<Filtre>(() => ({ ...FILTRE_VIDE, ...filtreInitial }))
  // Le filtre reçu à l'ouverture décide une fois pour toutes s'il faut proposer une semaine.
  const [proposerUneSemaine] = useState(() => !filtreInitial?.periode)

  useEffect(() => {
    let actif = true
    lireSessions()
      .then((lues) => {
        if (!actif) return
        setSessions(lues)
        const proposee = proposerUneSemaine ? semaineParDefaut(lues, aujourdhui()) : null
        if (proposee) setFiltre((f) => ({ ...f, ...proposee }))
      })
      .catch((e: unknown) => {
        if (actif) setErreur(e instanceof Error ? e.message : 'Lecture des sessions impossible')
      })
      .finally(() => {
        if (actif) setChargement(false)
      })
    return () => {
      actif = false
    }
  }, [proposerUneSemaine])

  const centres = useMemo(() => {
    const parId = new Map<string, string>()
    for (const s of sessions) parId.set(s.centreId, s.centre)
    return [...parId.entries()]
      .map(([id, nom]) => ({ id, nom }))
      .sort((a, b) => a.nom.localeCompare(b.nom, 'fr'))
  }, [sessions])

  const periodes = useMemo(() => periodesDisponibles(sessions, filtre.centreId), [sessions, filtre.centreId])
  const semaines = useMemo(
    () => semainesDisponibles(sessions, filtre.centreId, filtre.periode),
    [sessions, filtre.centreId, filtre.periode],
  )
  const filtrees = useMemo(() => filtrerSessions(sessions, filtre), [sessions, filtre])

  return { sessions, chargement, erreur, filtre, setFiltre, centres, periodes, semaines, filtrees }
}

export type SessionsGroupes = ReturnType<typeof useSessionsGroupes>
